/**
 * useCreateDataAgent Hook
 *
 * React Query mutation hook for creating a data agent.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { dataAgentService } from '../../../api/data-agent.service';
import { DATA_AGENTS_QUERY_KEY } from './useDataAgents';
import type { DataAgentCreateDTO, DataAgent } from '../../../types/data-agent.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseCreateDataAgentOptions {
  onSuccess?: (dataAgent: DataAgent) => void;
  onError?: (error: string) => void;
}

export function useCreateDataAgent(tenantId: string, options?: UseCreateDataAgentOptions) {
  const queryClient = useQueryClient();

  return useMutation<DataAgent, Error, DataAgentCreateDTO>({
    mutationFn: (data: DataAgentCreateDTO) =>
      dataAgentService.createDataAgent(tenantId, data),
    onSuccess: (dataAgent) => {
      queryClient.invalidateQueries({ queryKey: [DATA_AGENTS_QUERY_KEY, tenantId] });
      options?.onSuccess?.(dataAgent);
    },
    onError: (error) => {
      options?.onError?.(getErrorMessage(error));
    },
  });
}
